import React, { useCallback, useEffect, useState } from 'react';
import './CycleCountReport.scss';
import { Button, Form, Table } from 'react-bootstrap';
import { ReactSVG } from 'react-svg';
import exporticon from '../../../assets/images/export-icon.svg';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import { FormikHelpers, useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import useLocations from '../../../Hooks/useLocations';
import Loading from '../../../components/LoadingPage/Loading';
import { DropDownValues } from '../../../utils/types';
import useReports from '../../../Hooks/useReports';

const initialFormValues = {
    locationId: '',
    verifiedBefore: moment(Date()).toDate(),
};

const CycleCountReport: React.FC = () => {
    const [cycleCountList, setCycleCountList] = useState<any[]>([]);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [locationOptions, setLocationOptions] = useState<DropDownValues[]>([]);

    const { getLocationsAPI, getLocationsLoading } = useLocations();
    const { getCycleCountReportAPI, cycleCountReportLoading, exportCycleCountReportAPI, exportCycleCountReportLoading, exportCycleCountReportAsTallyAPI, exportCycleCountReportAsTallyLoading } = useReports();

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const res = await getLocationsAPI({
                searchText: '',
                active: true,
                sortColumn: 'locationName',
                sortDirection: '',
            });
            const options = res?.map((option: any) => ({
                value: option.id,
                label: option.locationName,
            }));
            setLocationOptions(options);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const fetchCycleCountReport = async (filterValues: any) => {
        try {
            const params: Record<string, any> = {};
            if (filterValues.locationId) params.locationId = filterValues.locationId;
            if (filterValues.verifiedBefore) params.verifiedBefore = moment(filterValues.verifiedBefore).format('YYYY-MM-DDTHH:mm:ss');

            const res = await getCycleCountReportAPI(params);
            setCycleCountList(res || []);
            setHasSearched(true);
        } catch (error: any) {
            console.error("Error while fetching item verification list: ", error);
        }
    };

    const { handleChange, values, handleSubmit, resetForm } =
        useFormik({
            initialValues: initialFormValues,
            validationSchema: Yup.object({}),
            onSubmit: async (values: any, { setSubmitting }: FormikHelpers<any>) => {
                try {
                    await fetchCycleCountReport(values);
                } catch (err: any) { }
                setSubmitting(false);
            },
            enableReinitialize: true,
        });

    const isFilterChanged = () => !isEqual(values, initialFormValues);

    const handleClear = () => {
        if (!isFilterChanged()) return;
        resetForm();
        fetchCycleCountReport(initialFormValues);
    };

    const downloadReport = useCallback(async (
        exportFn: (params: Record<string, any>) => Promise<any>,
        fileName: string,
    ) => {
        try {
            const params: Record<string, any> = {};
            if (values.locationId) params.locationId = values.locationId;
            if (values.verifiedBefore) params.verifiedBefore = moment(values.verifiedBefore).format('YYYY-MM-DDTHH:mm:ss');
            const res = await exportFn(params);
            const url = URL.createObjectURL(res);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `${localStorage.getItem("profileClientId")?.toUpperCase() ?? ""}_${fileName}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
        } catch (error: any) {
            console.error(`Error while exporting ${fileName}: `, error);
        }
    }, [values]);

    const handleExport = useCallback(() => {
        downloadReport(exportCycleCountReportAPI, "Cycle_Count_Report");
    }, [downloadReport]);

    const handleExportAsTally = useCallback(() => {
        downloadReport(exportCycleCountReportAsTallyAPI, "Cycle_Count_Tally_Sheet");
    }, [downloadReport]);

    const formatDate = (date: any) => {
        if (!date) return "-";
        return moment(date).format("MM/DD/YYYY hh:mm:ss A");
    };

    const formatUserName = (user: any) => {
        if (!user) return "";
        return `${user.lName} ${user.fName} ${user?.userName ? `(${user.userName})` : ''}`;
    };

    const formatLocation = (location: any) => {
        if (!location) return "";
        return `${location.locationName} ${location?.locationCode ? `(${location.locationCode})` : ''}`;
    };

    const isLoading = getLocationsLoading || cycleCountReportLoading;

    return (
        <div className='item-verification-list-main'>
            {isLoading && (<Loading />)}
            <div className="page-title">
                <h2>Cycle Count Report</h2>
            </div>
            <div className="report-filter-wrapper">
                <Form className='item-verification-list-main__filter-form' onSubmit={handleSubmit}>
                    <Form.Group className='form-group location-field'>
                        <Form.Label htmlFor='location_code'>Location</Form.Label>
                        <Select
                            isSearchable={true}
                            classNamePrefix="react-select"
                            options={locationOptions}
                            className="react-select-container"
                            id="location_code"
                            name="locationId"
                            value={locationOptions?.find((option: DropDownValues) => option.value === values.locationId) || null}
                            onChange={(option: any) => {
                                handleChange("locationId")(option?.value ?? '');
                            }}
                        />
                    </Form.Group>
                    <Form.Group className='form-group date-field'>
                        <Form.Label htmlFor='verified_before'>Verified Before</Form.Label>
                        <DatePicker
                            locale="en-US"
                            format="MM/dd/yyyy"
                            id="verified_before"
                            name='verifiedBefore'
                            value={values.verifiedBefore}
                            maxDate={new Date()}
                            openCalendarOnFocus={false}
                            showLeadingZeros={true}
                            dayPlaceholder="dd"
                            monthPlaceholder="mm"
                            yearPlaceholder="yyyy"
                            clearIcon={null}
                            calendarIcon={<i className="bi bi-calendar" />}
                            className="form-control"
                            onChange={(date: any) => {
                                handleChange({
                                    target: {
                                        name: "verifiedBefore",
                                        value: moment(date).isValid() ? moment(date).format("L") : "",
                                    },
                                });
                            }}
                        />
                    </Form.Group>
                    <Form.Group className='form-actions'>
                        <Button type='submit' variant='primary'>View</Button>
                        <Button type='button' variant='link' onClick={handleClear}>Clear</Button>
                    </Form.Group>
                </Form>
                <div className="report-export-box">
                    <button
                        type="button"
                        className="report-export-btn"
                        onClick={handleExport}
                    >
                        <ReactSVG className="svg-box" wrapper="span" src={exporticon} />
                        Export
                    </button>
                    <button
                        type="button"
                        className="report-export-btn"
                        onClick={handleExportAsTally}
                    >
                        <ReactSVG className="svg-box" wrapper="span" src={exporticon} />
                        Export as Tally
                    </button>
                </div>
            </div>

            {hasSearched && (
                <div className="item-verification-list-main__table-wrapper">
                    <Table responsive hover={true}>
                        <thead>
                            <tr>
                                <th>Location</th>
                                <th>Barcode</th>
                                <th>Item Name</th>
                                <th>NDC</th>
                                <th>LOT</th>
                                <th>Expiration Date</th>
                                <th className='text-end'>Quantity</th>
                                <th>Verified By</th>
                                <th>Verified Date</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cycleCountList?.length > 0 ? (
                                cycleCountList.map((cycleCountItem: any, index: number) => (
                                    <tr key={cycleCountItem?.id || index}>
                                        <td title={formatLocation(cycleCountItem?.location)}>{formatLocation(cycleCountItem?.location) || "-"}</td>
                                        <td title={cycleCountItem?.item?.rxSRXId || ""}>{cycleCountItem?.item?.rxSRXId || "-"}</td>
                                        <td title={cycleCountItem?.item?.rxName || ""}>{cycleCountItem?.item?.rxName || "-"}</td>
                                        <td title={cycleCountItem?.item?.ndcCode || ""}>{cycleCountItem?.item?.ndcCode || "-"}</td>
                                        <td title={cycleCountItem?.item?.lotNo || ""}>{cycleCountItem?.item?.lotNo || "-"}</td>
                                        <td title={formatDate(cycleCountItem?.item?.expiryDate)}>{formatDate(cycleCountItem?.item?.expiryDate)}</td>
                                        <td className='text-end' title={cycleCountItem?.quantity?.toString() || ""}>{cycleCountItem?.quantity ?? "-"}</td>
                                        <td title={formatUserName(cycleCountItem?.user)}>{formatUserName(cycleCountItem?.user) || "-"}</td>
                                        <td title={formatDate(cycleCountItem?.verifyDate)}>{formatDate(cycleCountItem?.verifyDate)}</td>
                                        <td title={cycleCountItem?.note || ""}>{cycleCountItem?.note || "-"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={9} className="text-center">
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    )
}

export default CycleCountReport;
